import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle2, 
  FileText, 
  Users, 
  Bell, 
  ShieldCheck, 
  Settings, 
  Cpu, 
  Database, 
  Activity, 
  Layers, 
  Battery, 
  Smartphone, 
  HelpCircle,
  Clock,
  Check
} from 'lucide-react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import VideoLibrary from '../components/VideoLibrary';

const MedimiSmartTim = () => {
  useDocumentMetadata({
    title: 'TIM Helhedsløsning | Intelligent medicindispenser | MedicCare',
    description: 'TIM (Totalløsning til Intelligent Medicinhåndtering) er en komplet løsning til medicinhåndtering, der forbinder læge, FMK, EOJ og borger i et ubrudt, sikkert kredsløb.'
  });

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 py-20 lg:py-32 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medimi_3.jpg" 
            alt="Medimi Smart TIM" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-[#2c3223]/85 mix-blend-multiply"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Link 
              to="/medimi-smart" 
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-soft hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={14} /> Tilbage til Produkter
            </Link>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
              Medimi®Smart (TIM)
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed">
              TIM-løsningen er mere end blot en intelligent pilledispenser. Gennem integration til omsorgssystemerne får du en helhedsløsning fra lægens ordination til borgerens hånd.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="/#kontakt" 
                className="bg-white text-[#2c3223] text-xs font-bold uppercase tracking-widest px-8 py-4 rounded-md hover:bg-brand-soft transition-all"
              >
                Book en demonstration
              </a>
              <a 
                href="#kaeden" 
                className="bg-transparent border border-white/30 text-white text-xs font-bold uppercase tracking-widest px-8 py-4 rounded-md hover:bg-white/10 transition-all"
              >
                Se hvordan det virker
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Hvad er TIM? & Dokumenteret Effekt */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Hvad er TIM */}
            <div className="lg:col-span-7">
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                Introduktion
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                Hvad er TIM?
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed text-lg">
                TIM står for <strong>“Totalløsning til Intelligent Medicinhåndtering”</strong>. TIMs officielle produktnavn er <strong>MedimiSmart</strong>, men i daglig tale kaldes den blot for TIM.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Løsningen består af et administrationssystem (FlexSuite) og en intelligent medicinrobot, der placeres i borgerens eget hjem. TIM er designet til at skabe tryghed, øge selvstændigheden og frigøre værdifulde ressourcer i den kommunale hjemmepleje.
              </p>
              <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-brand mt-1 shrink-0">
                  <i className="fa-solid fa-circle-info text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">Ubrudt og sikker værdikæde</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    TIM er fuldt integreret med omsorgssystemerne og henter medicininformation direkte fra LMK (FMK). Enhver justering (op/ned) i dosis slår igennem i realtid helt ud til maskinen, så dosis er ændret ved næste tryk på knappen.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Dokumenteret Effekt Card */}
            <div className="lg:col-span-5 lg:sticky lg:top-8">
              <div className="bg-[#2c3223] text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                {/* Decorative background elements */}
                <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-light/10 rounded-full blur-2xl"></div>
                <div className="absolute -left-10 -top-10 w-40 h-40 bg-brand-soft/5 rounded-full blur-2xl"></div>
                
                <div className="relative z-10">
                  <div className="bg-brand-light/20 text-brand-soft px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 mb-6">
                    <i className="fa-solid fa-chart-line"></i> Dokumenteret Effekt
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4">KL & Deloitte Caseanalyse</h3>
                  <p className="text-slate-200 text-sm leading-relaxed mb-6">
                    TIM-løsningen har været i fuld drift siden <strong>2018</strong> og er dokumenteret som en yderst tidsbesparende teknologi med stor dokumenteret effekt for kommunerne.
                  </p>
                  
                  <div className="bg-white/10 p-4 rounded-xl mb-6 border border-white/10">
                    <div className="text-3xl font-black text-white mb-1">99,8% +</div>
                    <div className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Dokumenteret korrekt dosering</div>
                  </div>

                  <p className="text-xs text-slate-300 italic mb-6">
                    * Kilde: KL/Deloitte Caseanalyse, september 2022.
                  </p>
                  
                  <a 
                    href="https://mediccare.sharepoint.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full bg-white text-[#2c3223] text-xs font-bold uppercase tracking-widest py-4 px-6 rounded-md hover:bg-brand-soft transition-all flex items-center justify-center gap-2 shadow-md"
                  >
                    <FileText size={16} />
                    Læs analysen på SharePoint
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Målgruppe & Kredsløbet (Sammensat sektion) */}
      <section id="maelgruppe" className="py-20 bg-slate-50 border-t border-slate-200 scroll-mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Del 1: Målgruppe */}
          <div>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                Målgruppe
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Hvilke borgere kan bruge TIM-løsningen?</h2>
              <p className="text-slate-600">
                TIM er udviklet til at støtte borgere i eget hjem, som har brug for hjælp til medicinhåndtering, men som fortsat kan bevare en høj grad af selvstændighed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Kriterium 1 */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex gap-6 items-start">
                <div className="bg-brand-soft p-4 rounded-xl text-[#2c3223] shrink-0">
                  <i className="fa-solid fa-brain text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Kognitiv forståelse</h3>
                  <div className="space-y-3">
                    <p className="text-slate-600 text-sm leading-relaxed">
                      Borgeren skal kognitivt være i stand til at forstå, at når den <strong>“grønne lampe”</strong> lyser på maskinen, betyder det, at det nu er tid til at tage sin medicin.
                    </p>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      Borgeren skal kunne enten læse eller høre besked &amp; forstå en besked.
                    </p>
                  </div>
                </div>
              </div>

              {/* Kriterium 2 */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex gap-6 items-start">
                <div className="bg-brand-soft p-4 rounded-xl text-[#2c3223] shrink-0">
                  <i className="fa-solid fa-hand-pointer text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Fysisk formåen</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Borgeren skal fysisk være i stand til at trykke på den grønne knap, tage medicinkoppen ud af maskinen, indtage sin medicin og efterfølgende stille koppen korrekt tilbage i maskinen.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Kredsløbet (Fuld bredde hvid baggrund) */}
      <section id="kaeden" className="py-20 bg-white border-y border-slate-200 scroll-mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
              Kredsløbet
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Overblik over TIM-løsningen – Kæden</h2>
            <p className="text-slate-600">
              En fuldstændig integreret og sikker kæde, der sikrer, at borgeren altid får den rette medicin til tiden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 relative">
            {/* Step 1 */}
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200/60 flex flex-col justify-between relative shadow-sm">
              <div>
                <div className="text-xs font-bold text-[#2c3223] uppercase tracking-wider mb-2">Trin 01</div>
                <h4 className="font-bold text-slate-900 mb-3 text-lg">Lægeordination</h4>
                <p className="text-slate-600 text-xs leading-relaxed">
                  Lægen ordinerer og udskriver recepter helt som normalt. Løsningen er <strong>ikke</strong> baseret på dosisposer, hvilket giver maksimal fleksibilitet.
                </p>
              </div>
              <div className="mt-6 self-end">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/chatgpt-image-2-jul-2026-08-53-35.png" 
                  alt="Læge" 
                  className="w-10 h-10 object-contain" 
                />
              </div>
            </div>

              {/* Step 2 */}
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200/60 flex flex-col justify-between relative shadow-sm">
                <div>
                  <div className="text-xs font-bold text-[#2c3223] uppercase tracking-wider mb-2">Trin 02</div>
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">Apotekspakning</h4>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Apoteket modtager medicininformation fra de traditionelle systemer integreret med vores Flex-system. Herefter pakkes medicinen i vores specialkassetter.
                  </p>
                </div>
                <div className="mt-6 self-end">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medidcin.png" 
                    alt="Apotek" 
                    className="w-10 h-10 object-contain" 
                  />
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200/60 flex flex-col justify-between relative shadow-sm">
                <div>
                  <div className="text-xs font-bold text-[#2c3223] uppercase tracking-wider mb-2">Trin 03</div>
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">Udbringning</h4>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Apoteket bringer kassetterne direkte ud til borgeren og opfylder maskinen i hjemmet enten hver <strong>7., 14. eller 28. dag</strong>.
                  </p>
                </div>
                <div className="mt-6 self-end">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/bil.png" 
                    alt="Udbringning" 
                    className="w-10 h-10 object-contain" 
                  />
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200/60 flex flex-col justify-between relative shadow-sm">
                <div>
                  <div className="text-xs font-bold text-[#2c3223] uppercase tracking-wider mb-2">Trin 04</div>
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">TIM i Hjemmet</h4>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    MedimiSmart (TIM) står i borgerens hjem. Den er ekstremt brugervenlig med <strong>kun 1 knap</strong> og guider borgeren sikkert igennem dispenseringen.
                  </p>
                </div>
                <div className="mt-6 self-end">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png" 
                    alt="TIM" 
                    className="w-10 h-10 object-contain" 
                  />
                </div>
              </div>

              {/* Step 5 */}
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200/60 flex flex-col justify-between relative shadow-sm">
                <div>
                  <div className="text-xs font-bold text-[#2c3223] uppercase tracking-wider mb-2">Trin 05</div>
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">Sundhedssupport</h4>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Vores sundhedssupport overvåger systemet online. Hvis noget afviger, kontaktes borgeren med det samme, og borgeren kan altid ringe ind for hjælp.
                  </p>
                </div>
                <div className="mt-6 self-end">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/support.png" 
                    alt="Sundhedssupport" 
                    className="w-10 h-10 object-contain" 
                  />
                </div>
              </div>
            </div>

        </div>
      </section>

      {/* De 5 Påmindelsesmetoder */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[#1e2318]/90 z-0"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-semibold uppercase tracking-wider bg-brand-light/20 text-brand-soft rounded-full border border-brand-light/10">
              Sikkerhed & Compliance
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">Op til 5 forskellige påmindelsesmuligheder</h2>
            <p className="text-slate-300">
              For at sikre maksimal medicinsikkerhed og compliance, kan TIM påminde borgeren på op til fem forskellige måder, tilpasset den enkeltes behov.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            
            {/* Påmindelse 1 */}
            <div className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-all">
              <div className="text-brand-soft mb-4">
                <i className="fa-solid fa-lightbulb text-3xl"></i>
              </div>
              <h4 className="font-bold text-lg mb-2 text-white">1. Lys</h4>
              <p className="text-slate-300 text-xs leading-relaxed">
                En tydelig, blinkende grøn lampe på maskinen indikerer visuelt, at det er tid til medicin.
              </p>
            </div>

            {/* Påmindelse 2 */}
            <div className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-all">
              <div className="text-brand-soft mb-4">
                <i className="fa-solid fa-volume-high text-3xl"></i>
              </div>
              <h4 className="font-bold text-lg mb-2 text-white">2. Tale</h4>
              <p className="text-slate-300 text-xs leading-relaxed">
                Medicinrobotten taler direkte til borgeren og guider med en klar stemme igennem processen.
              </p>
            </div>

            {/* Påmindelse 3 */}
            <div className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-all">
              <div className="text-brand-soft mb-4">
                <i className="fa-solid fa-comment-sms text-3xl"></i>
              </div>
              <h4 className="font-bold text-lg mb-2 text-white">3. SMS</h4>
              <p className="text-slate-300 text-xs leading-relaxed">
                Der kan sendes en automatisk sms-påmindelse direkte til borgerens eller pårørendes telefon.
              </p>
            </div>

            {/* Påmindelse 4 */}
            <div className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-all">
              <div className="text-brand-soft mb-4">
                <i className="fa-solid fa-robot text-3xl"></i>
              </div>
              <h4 className="font-bold text-lg mb-2 text-white">4. RVR</h4>
              <p className="text-slate-300 text-xs leading-relaxed">
                Robot med personlig påmindelse, der kan skræddersys specifikt til borgerens daglige rutiner.
              </p>
            </div>

            {/* Påmindelse 5 */}
            <div className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-all">
              <div className="text-brand-soft mb-4">
                <i className="fa-solid fa-headset text-3xl"></i>
              </div>
              <h4 className="font-bold text-lg mb-2 text-white">5. Sundhedssupport</h4>
              <p className="text-slate-300 text-xs leading-relaxed">
                En personlig opringning fra en medarbejder i vores sundhedssupport, hvis medicinen ikke tages.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Nøglespecifikationer & Lovgivning */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Left Column: Nøglespecifikationer */}
            <div className="lg:col-span-7">
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                Specifikationer
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-8 leading-tight">
                Tekniske specifikationer & Sikkerhed
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                
                <div className="flex gap-4">
                  <div className="text-brand shrink-0 mt-1">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png" 
                      alt="TIM" 
                      className="w-6 h-6 object-contain" 
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">3 størrelser</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      TIM-robotten fås i 3 størrelser, der kan rumme henholdsvis <strong>8, 12 eller 16</strong> forskellige præparater.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-brand shrink-0 mt-1">
                    <i className="fa-solid fa-battery-three-quarters text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Backupbatteri</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Indbygget backupbatteri sikrer fuld drift i op til <strong>24 timer</strong> ved eventuel strømafbrydelse.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-brand shrink-0 mt-1">
                    <i className="fa-solid fa-rotate text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Realtidsjustering</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Dosisændringer slår igennem med det samme. Ingen ventetid eller risiko for forældede dosisposer.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-brand shrink-0 mt-1">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsuite.png" 
                      alt="FlexSuite" 
                      className="w-6 h-6 object-contain" 
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Online opkoblet</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      TIM er konstant online opkoblet via en sikker router direkte til administrationssystemet <strong>FlexSuite</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-brand shrink-0 mt-1">
                    <i className="fa-solid fa-scale-balanced text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Gældende lovgivning</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Løsningen overholder alle gældende lovgivninger, er <strong>CE-godkendt</strong> og klassificeret som medicinsk udstyr.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-brand shrink-0 mt-1">
                    <i className="fa-solid fa-key text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Sikkert login</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Understøtter to-faktor login (SMS) samt direkte login via <strong>FK-adgang / Kombit</strong>.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Column: Fleksible muligheder / Optioner */}
            <div className="lg:col-span-5 bg-slate-50 p-8 rounded-2xl border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <i className="fa-solid fa-sliders text-brand"></i> Fleksible muligheder i TIM
              </h3>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="bg-brand/10 text-brand p-1 rounded-full shrink-0 mt-0.5">
                    <Check size={14} className="stroke-[3]" />
                  </div>
                  <div>
                    <strong className="text-slate-900 text-sm block">Medicin på forhånd</strong>
                    <span className="text-slate-600 text-xs">Mulighed for nemt at tage medicin ud på forhånd, f.eks. hvis borgeren skal på udflugt.</span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="bg-brand/10 text-brand p-1 rounded-full shrink-0 mt-0.5">
                    <Check size={14} className="stroke-[3]" />
                  </div>
                  <div>
                    <strong className="text-slate-900 text-sm block">Behovsmedicin</strong>
                    <span className="text-slate-600 text-xs">Understøtter sikker og kontrolleret administration af borgerens behovsmedicin.</span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="bg-brand/10 text-brand p-1 rounded-full shrink-0 mt-0.5">
                    <Check size={14} className="stroke-[3]" />
                  </div>
                  <div>
                    <strong className="text-slate-900 text-sm block">Vederlagsfri medicin</strong>
                    <span className="text-slate-600 text-xs">Mulighed for fuld håndtering af vederlagsfri medicin direkte i systemet.</span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="bg-brand/10 text-brand p-1 rounded-full shrink-0 mt-0.5">
                    <Check size={14} className="stroke-[3]" />
                  </div>
                  <div>
                    <strong className="text-slate-900 text-sm block">Remote dispenseringshjælp</strong>
                    <span className="text-slate-600 text-xs">Mulighed for fjernstyret dispenseringshjælp via sundhedssupporten.</span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="bg-brand/10 text-brand p-1 rounded-full shrink-0 mt-0.5">
                    <Check size={14} className="stroke-[3]" />
                  </div>
                  <div>
                    <strong className="text-slate-900 text-sm block">Feriekassetter</strong>
                    <span className="text-slate-600 text-xs">Særlige feriekassetter gør det nemt og sikkert for borgeren at rejse på ferie.</span>
                  </div>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Video Library (uden autoplay på TIM siden) */}
      <VideoLibrary productId="tim" layout="standard" enableAutoplay={false} />

      {/* CTA Section */}
      <section className="bg-[#2c3223] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Vil du vide mere om Medimi®Smart (TIM)?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Vi tilbyder uforpligtende demonstrationer for kommuner, regioner og sundhedsinstitutioner. Kontakt os i dag for at høre, hvordan vi kan hjælpe jer.
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href="/#kontakt" 
              className="bg-white text-[#2c3223] font-bold text-xs uppercase tracking-widest px-8 py-4 rounded-full hover:bg-brand-soft transition-colors"
            >
              Kontakt os nu
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MedimiSmartTim;
